/**
 * Marketplace billing (hosted checkout) + seller onboarding (org).
 *
 * BE returns RAW responses (no `{ data }` wrapper).
 */
import type {
  MarketplacePayoutPage,
  PayoutStatementPage,
  PayoutStatementDocumentDto,
  PayoutStatementIssueResult,
} from '@mfw/common';

import type {
  ApiMarketplaceHistoryPage,
  ApiMarketplacePurchaseRow,
  ApiMarketplaceSaleRow,
  FetchMarketplacePurchasesArgs,
  FetchMarketplaceSalesArgs,
  AdminOnboardingBeneficiaryArgs,
  ApiRecipientOnboardingAccountCountries,
  RequestRecipientOnboardingArgs,
  ApiRecipientOnboardingStatus,
  ApiRequestOnboardingResult,
  AdminOnboardingReasonArgs,
  MarketplaceCheckoutArgs,
  ApiRecipientOnboarding,
  ApiAdminOnboardingRow,
  ApiRedirectResult,
} from './types/payments.types';

import { post, get } from './axios';

export type FetchOrgMarketplacePayoutsArgs = {
  organizationId: string;
  limit?: number;
  offset?: number;
};

/** The organization's marketplace payouts, newest first — raw `{ rows, total, limit, offset }`. */
export const fetchOrgMarketplacePayouts = async ({
  organizationId,
  limit,
  offset,
}: FetchOrgMarketplacePayoutsArgs): Promise<MarketplacePayoutPage> => {
  const response = await get<MarketplacePayoutPage>(
    `/v2/organizations/${organizationId}/marketplace-payouts`,
    {
      query: { limit: limit?.toString(), offset: offset?.toString() },
    },
  );
  return (
    response ?? { limit: limit ?? 0, offset: offset ?? 0, rows: [], total: 0 }
  );
};

export type FetchOrgPayoutStatementsArgs = {
  organizationId: string;
  limit?: number;
  offset?: number;
};

/** The organization's issued payout statements, newest month first (Q6/Task 11-12). */
export const fetchOrgPayoutStatements = async ({
  organizationId,
  limit,
  offset,
}: FetchOrgPayoutStatementsArgs): Promise<PayoutStatementPage> => {
  const response = await get<PayoutStatementPage>(
    `/v2/organizations/${organizationId}/marketplace-payouts/statements`,
    { query: { limit: limit?.toString(), offset: offset?.toString() } },
  );
  return response ?? { data: [], total: 0 };
};

export type IssuePayoutStatementArgs = {
  organizationId: string;
  period: string;
  currency: string;
};

/**
 * Issues the statement for one closed month, or hands back the one that
 * already exists — the route is idempotent (Task 11). Downloading an
 * already-issued statement does NOT come through here: that is
 * `fetchOrgPayoutStatementDocument` below, a read that creates nothing.
 */
export const issueOrgPayoutStatement = async ({
  organizationId,
  period,
  currency,
}: IssuePayoutStatementArgs): Promise<PayoutStatementIssueResult> => {
  const response = await post(
    `/v2/organizations/${organizationId}/marketplace-payouts/statements/${period}/${currency}`,
    {},
  );
  // the `post` helper types a `{ data }` wrapper, but this route (like its
  // siblings on `MarketplacePayoutsController`) returns a raw body — re-cast.
  return response as unknown as PayoutStatementIssueResult;
};

export type FetchPayoutStatementDocumentArgs = {
  organizationId: string;
  period: string;
  currency: string;
};

/**
 * DD2 — a fresh, short-lived (120s) link to the PDF of a statement that was
 * ALREADY issued. A pure read: it 404s a month nobody stated and never issues
 * one. Its own route because the issue route bans a platform account (P2),
 * which may nevertheless read every statement of its tenant.
 */
export const fetchOrgPayoutStatementDocument = async ({
  organizationId,
  period,
  currency,
}: FetchPayoutStatementDocumentArgs): Promise<PayoutStatementDocumentDto> => {
  const response = await get<PayoutStatementDocumentDto>(
    `/v2/organizations/${organizationId}/marketplace-payouts/statements/${period}/${currency}/document`,
  );
  return response as PayoutStatementDocumentDto;
};

export type FetchPayoutStatementCsvArgs = {
  organizationId: string;
  period: string;
  currency: string;
};

/**
 * Task 3.1 — the CSV of a statement that was ALREADY issued: a straight blob
 * download (same shape as `fetchStatsExport`/`fetchOrgWorkExport`), unlike
 * the PDF sibling above which hands back a short-lived presigned URL. The
 * backend builds the CSV fresh from the frozen row on every call, so there
 * is no link to mint or cache here — the bytes themselves are the response.
 */
export const fetchOrgPayoutStatementCsv = async ({
  organizationId,
  period,
  currency,
}: FetchPayoutStatementCsvArgs): Promise<Blob | null> => {
  const response = await get<Blob>(
    `/v2/organizations/${organizationId}/marketplace-payouts/statements/${period}/${currency}/csv`,
    { responseType: 'blob' },
  );
  return response ?? null;
};

export const marketplaceCheckout = async ({
  cancelUrl,
  marketplaceItemId,
  acceptedTermsVersionId,
  successUrl,
}: MarketplaceCheckoutArgs) => {
  const response = await post('/v2/payments/marketplace/checkout', {
    cancelUrl,
    marketplaceItemId,
    acceptedTermsVersionId,
    successUrl,
  });
  // the `post` helper types a `{ data }` wrapper, but the BE returns a raw body here — re-cast.
  return (response ?? null) as ApiRedirectResult | null;
};

/** The caller's own paid purchases, newest first — raw `{ rows, total, limit, offset }`. */
export const fetchMarketplacePurchases = async ({
  limit,
  offset,
  orderRef,
}: FetchMarketplacePurchasesArgs = {}) => {
  const response = await get<
    ApiMarketplaceHistoryPage<ApiMarketplacePurchaseRow>
  >('/v2/payments/marketplace/purchases', {
    query: { limit: limit?.toString(), offset: offset?.toString(), orderRef },
  });
  return response ?? null;
};

/** The organization's sales with the full split and payout state, newest first. */
export const fetchMarketplaceSales = async ({
  limit,
  offset,
  organizationId,
}: FetchMarketplaceSalesArgs) => {
  const response = await get<ApiMarketplaceHistoryPage<ApiMarketplaceSaleRow>>(
    `/v2/organizations/${organizationId}/marketplace-sales`,
    { query: { limit: limit?.toString(), offset: offset?.toString() } },
  );
  return response ?? null;
};

export const fetchRecipientOnboarding = async ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const response = await get<ApiRecipientOnboardingStatus>(
    `/v2/organizations/${organizationId}/recipient-onboarding`,
  );
  return response ?? null;
};

/**
 * 9.6 — the currency a PAID listing of this organization ends up in (RSD for a
 * PaySpot seller, otherwise EUR). Readable by any member, unlike the status
 * above, so every publishing role sees the right price floor.
 */
export const fetchMarketplaceListingCurrency = async ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const response = await get<{ currency: 'EUR' | 'RSD' }>(
    `/v2/organizations/${organizationId}/recipient-onboarding/listing-currency`,
  );
  return response ?? null;
};

/** The Stripe account-country picker's list — the one onboarding read that calls Stripe. */
export const fetchRecipientOnboardingAccountCountries = async ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const response = await get<ApiRecipientOnboardingAccountCountries>(
    `/v2/organizations/${organizationId}/recipient-onboarding/account-countries`,
  );
  return response ?? null;
};

/** The Stripe branch returns `redirectUrl` (Express link); the PaySpot branch has no redirect. */
export const requestRecipientOnboarding = async ({
  organizationId,
  accountCountry,
  refreshUrl,
  returnUrl,
}: RequestRecipientOnboardingArgs) => {
  const response = await post(
    `/v2/organizations/${organizationId}/recipient-onboarding`,
    { refreshUrl, returnUrl, ...(accountCountry ? { accountCountry } : {}) },
  );
  return (response ?? null) as ApiRequestOnboardingResult | null;
};

// ── Superadmin ops layer (R-AUD-69) — responses in a `{ data }` wrapper ─────

const ADMIN_ONBOARDING_PATH = '/v2/payments/admin/recipient-onboarding';

export const fetchAdminOnboardings = async ({
  status,
}: {
  status?: string;
} = {}) => {
  const response = await get<{ data: ApiAdminOnboardingRow[] }>(
    ADMIN_ONBOARDING_PATH,
    { query: { status } },
  );
  return response?.data ?? [];
};

export const adminOnboardingContractPending = async ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const response = await post<ApiRecipientOnboarding>(
    `${ADMIN_ONBOARDING_PATH}/${organizationId}/contract-pending`,
    {},
  );
  return response?.data ?? null;
};

export const adminOnboardingBeneficiary = async ({
  organizationId,
  ...body
}: AdminOnboardingBeneficiaryArgs) => {
  const response = await post<ApiRecipientOnboarding>(
    `${ADMIN_ONBOARDING_PATH}/${organizationId}/beneficiary`,
    body,
  );
  return response?.data ?? null;
};

export const adminOnboardingSuspend = async ({
  organizationId,
  reason,
}: AdminOnboardingReasonArgs) => {
  const response = await post<ApiRecipientOnboarding>(
    `${ADMIN_ONBOARDING_PATH}/${organizationId}/suspend`,
    { reason },
  );
  return response?.data ?? null;
};

export const adminOnboardingReject = async ({
  organizationId,
  reason,
}: AdminOnboardingReasonArgs) => {
  const response = await post<ApiRecipientOnboarding>(
    `${ADMIN_ONBOARDING_PATH}/${organizationId}/reject`,
    { reason },
  );
  return response?.data ?? null;
};
